import {
  ComponentPropsWithoutRef,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { RemoveScroll } from "react-remove-scroll";
import { IconX } from "@tabler/icons-react";
import { SwipeableDrawer } from "@mui/material";
import { isIOSApp } from "../../../tools/ios";
import { isAndroidApp } from "../../../tools/android";

/**
 * Every height a bottom sheet may take, capped at 92dvh: past that the panel
 * crowds the iOS status bar (no breathing room above the handle). Expressed in
 * `dvh` only — `vh` measures the LARGEST viewport, so an "85vh" panel is taller
 * than 85% of what the user can actually see and cannot be compared to the
 * ceiling. Add a value here (≤ 92dvh) instead of widening this back to `string`.
 */
type SheetHeight = "83dvh" | "85dvh" | "90dvh" | "92dvh";

type Props = ComponentPropsWithoutRef<"div"> & {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  displayCloseButton?: boolean;
  actions?: ReactNode;
  /** Change this to scroll the content back to the top — a sheet that swaps one
   *  view for another inside the same scroll box keeps its `scrollTop`, so the
   *  second view opens wherever the first was left. Pass the active view's id. */
  scrollResetKey?: string;
  /** Height of the bottom panel. Omitted, it hugs its content (max 80dvh). */
  height?: SheetHeight;
  /** Apply a blur effect to the backdrop */
  backdropBlur?: boolean;
  /** The content owns its own bottom edge: drops the 20px bottom padding AND the
   *  scroll container's bottom fog. Both exist to mark "there is more below", and
   *  a pinned footer says it better — while the fog actively fights it, because
   *  `scroll-fog-y` is a MASK on the scroll box, so it fades out the bottom of
   *  anything sticky inside it and shows the panel through the hole. */
  disableBottomPadding?: boolean;
};

// TODO: Use compound components for close button & actions.

function useDefaultHeight() {
  const [defaultHeight, setDefaultHeight] = useState<SheetHeight | null>(null);

  useEffect(() => {
    if (!isAndroidApp()) {
      setDefaultHeight(null);
      return;
    }

    const updateHeight = () => {
      const viewportHeight = window.innerHeight;
      const availableHeight = window.visualViewport?.height ?? viewportHeight;
      const height = viewportHeight - availableHeight;

      if (height > 50) {
        setDefaultHeight("85dvh");
      } else {
        setDefaultHeight(null);
      }
    };

    updateHeight();

    window.addEventListener("resize", updateHeight);
    window.addEventListener("focusin", updateHeight);
    window.addEventListener("focusout", () => {
      setDefaultHeight(null);
    });

    const interval = setInterval(updateHeight, 100);

    return () => {
      window.removeEventListener("resize", updateHeight);
      window.removeEventListener("focusin", updateHeight);
      window.removeEventListener("focusout", () => {
        setDefaultHeight(null);
      });
      clearInterval(interval);
    };
  }, []);

  return defaultHeight;
}

export default function BottomPanel(props: Props) {
  const {
    isOpen,
    onClose,
    title,
    displayCloseButton,
    actions,
    height: heightProp,
    backdropBlur,
    disableBottomPadding,
    scrollResetKey,
  } = props;

  const defaultHeight = useDefaultHeight();
  const effectiveHeight = defaultHeight || heightProp;

  // Sanctioned DS machinery: the panel owns the scroll box, so it is the only
  // place that can reset it. A business component would have to reach for the
  // DOM to do the same.
  const contentRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (scrollResetKey === undefined) return;
    contentRef.current?.scrollTo({ top: 0 });
  }, [scrollResetKey]);

  return (
    <SwipeableDrawer
      anchor="bottom"
      disableSwipeToOpen
      disableBackdropTransition={!isIOSApp}
      open={isOpen}
      onOpen={() => {}}
      onClose={() => {
        onClose();
      }}
      onClick={(e) => {
        e.stopPropagation();
      }}
      sx={{
        "& .MuiDrawer-paper": {
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          paddingBottom: `env(safe-area-inset-bottom)`,
          top: effectiveHeight ? `calc(100dvh - ${effectiveHeight})` : "auto",
          "@media (min-width: 768px)": {
            left: "50% !important",
            right: "auto !important",
            transform: "translateX(-50%) !important",
            maxWidth: "600px",
            width: "600px",
          },
          "@media (min-width: 1024px)": {
            top: "50% !important",
            bottom: "auto !important",
            transform: "translate(-50%, -50%) !important",
            borderRadius: "20px !important",
            maxHeight: "80dvh",
          },
        },
        // The container's height lives HERE rather than in an inline style, so
        // that it can honour the same 1024px cap as the paper above it. It used
        // to be inline at a flat `height: <effectiveHeight>`, which on desktop
        // made the container TALLER than the sheet containing it: a 92dvh box
        // inside an 80dvh paper. Everything sized against that box then hung 12dvh
        // below the visible edge — including a `sticky bottom-0` footer, which is
        // why the annotation sheet's buttons were pinned off-screen on desktop and
        // correct on mobile.
        // Kill the edge fade when the content owns its bottom edge. The fade is
        // one variable for both edges (`--edge-fade`, App.css), so the top cue
        // goes with it — a fair trade against a mask eating a pinned footer.
        ...(disableBottomPadding
          ? { "& .bottom-panel-content": { "--edge-fade": "0px" } }
          : {}),
        "& .bottom-panel-container": {
          height: effectiveHeight || "auto",
          maxHeight: effectiveHeight || "80dvh",
          "@media (min-width: 1024px)": {
            maxHeight: "80dvh",
            height: effectiveHeight ? `min(${effectiveHeight}, 80dvh)` : "auto",
          },
        },
        "& .MuiBackdrop-root": {
          backdropFilter: backdropBlur ? "blur(8px)" : undefined,
          WebkitBackdropFilter: backdropBlur ? "blur(8px)" : undefined,
        },
      }}
    >
      <RemoveScroll enabled={isOpen}>
        <div
          className="bottom-panel-container relative w-full pt-[20px] flex flex-col overflow-hidden"
          style={{ paddingBottom: disableBottomPadding ? 0 : `20px` }}
        >
          <div className="bottom-panel-handle absolute top-[8px] inset-x-0  w-[36px] h-[3px] mx-auto rounded-[2px] bg-[#0002] transform translateX(-50%)" />

          {displayCloseButton && (
            <button
              type="button"
              onClick={() => {
                onClose();
              }}
              className="absolute top-[20px] right-[22px] z-10 flex items-center justify-center size-7 rounded-full bg-[#ededed] text-ds-black cursor-pointer border-none"
            >
              <IconX size={20} />
            </button>
          )}

          {title && (
            <>
              <div className="bottom-panel-header px-[22px] py-[16px] flex">
                <span className="flex-1 text-center text-[18px] font-semibold text-primary-black">
                  {title}
                </span>
              </div>
              <hr className="mx-[36px] border-t border-[#DFE6EC]" />
            </>
          )}

          <div
            ref={contentRef}
            className="bottom-panel-content scroll-fog-y relative flex-1 flex flex-col overflow-y-auto min-h-0"
          >
            {props.children}
          </div>

          {actions && (
            <div
              className="bottom-panel-actions px-[30px] py-[16px] shadow-[-4px_-4px_4px_rgba(33,33,33,0.07)] flex flex-col gap-[8px] bg-white shrink-0"
              style={{
                paddingBottom: `calc(16px + var(--safe-area-bottom))`,
              }}
            >
              {actions}
            </div>
          )}
        </div>
      </RemoveScroll>
    </SwipeableDrawer>
  );
}
